<script lang="ts">
  import {createEventDispatcher} from 'svelte';
  import InlineSVG from 'svelte-inline-svg';

  import type {ImageFile} from '../../types/project';

  import removeIcon from '../../assets/icons/circle-with-cross.svg';
  import cropIcon from '../../assets/icons/crop.svg';
  import hairCrossIcon from '../../assets/icons/hair-cross.svg';

  import IconButton from '../icon-button/IconButton.svelte';
  import ImageCropper from '../image-cropper/ImageCropper.svelte';
  import ImageFocuser from '../image-focuser/ImageFocuser.svelte';

  const dispatch = createEventDispatcher();

  export let image: ImageFile;
  export let caption = '';
  export let cropable = false;
  export let focusEditable = false;
  export let deletable = false;

  let showImageCropper = false;
  let showImageFocuser = false;
</script>

<style lang="postcss">
  .container {
    display: grid;
    grid-template-columns: 4.2rem 1fr;
    gap: 0.5rem;
  }

  @media (min-width: 640px) {
    .container {
      grid-template-columns: 1fr 3fr;
    }
  }

  .imageWrapper {
    position: relative;
    flex-grow: 1;
    flex-shrink: 0;
    width: 100%;
  }

  .inputWrapper input {
    width: 100%;
  }

  .image {
    padding-bottom: calc(100% / 16 * 9); /* aspect ratio 16:9 */
    border: 1px solid var(--ubilabs-gray);
    background-size: cover;
    background-repeat: no-repeat;
  }

  .imageActions {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(var(--ubilabs-anthracite-rgb), 0);
    color: var(--ubilabs-white);
  }

  .imageActions:hover {
    background-color: rgba(var(--ubilabs-anthracite-rgb), 0.5);
  }

  .action {
    position: relative;
    display: inline-flex;
    width: calc(var(--ubilabs-icon-button-icon-size) * 2);
    height: calc(var(--ubilabs-icon-button-icon-size) * 2);
    padding: calc(var(--ubilabs-icon-button-icon-size) / 2);
    background: none;
    outline: none;
    border: none;
    border-radius: var(--ubilabs-icon-button-icon-size);
    color: var(--ubilabs-white);
    font-size: var(--ubilabs-icon-button-icon-size);
    line-height: var(--ubilabs-icon-button-line-height);
    opacity: 0.6;
    pointer-events: all;
  }

  .action:hover,
  .action:focus-visible {
    opacity: 1;
  }

  .action::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: var(--ubilabs-icon-button-icon-size);
    background-color: var(--ubilabs-gray-dark-2);
    opacity: 0.4;
  }

  .action:hover::before,
  .action:focus::before {
    background-color: var(--ubilabs-gray-dark-1);
    opacity: 0.8;
  }

  .action :global(svg) {
    z-index: 1;
  }

  .inputWrapper {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
  }

  @media (min-width: 768px) {
    .inputWrapper {
      width: 100%;
    }
  }

  :global(.deleteButton) {
    margin: calc(
        (var(--ubilabs-input-height) - var(--ubilabs-icon-button-height)) / 2
      )
      0;
  }

  @media (min-width: 768px) {
    :global(.deleteButton) {
      margin-left: -1.25rem;
    }
  }
</style>

{#if image}
  <div class="container">
    <div class="imageWrapper">
      <div
        class="image"
        style="background-image: url('{image.url}'); background-position:
              {image.focusPoint ? image.focusPoint.x * 100 : 50}%
              {image.focusPoint ? image.focusPoint.y * 100 : 50}%;"
        title={caption} />

      {#if cropable || focusEditable}
        <div class="imageActions">
          {#if cropable}
            <div class="action" on:click={() => (showImageCropper = true)}>
              <InlineSVG src={cropIcon} />
            </div>
          {/if}
          {#if focusEditable}
            <div class="action" on:click={() => (showImageFocuser = true)}>
              <InlineSVG src={hairCrossIcon} />
            </div>
          {/if}
        </div>
      {/if}
    </div>
    <div class="inputWrapper">
      <input
        bind:value={caption}
        type="text"
        name={caption}
        placeholder="Image caption"
        maxlength="250"
        on:change={() => {
          dispatch('changecaption', {name: image.name, caption});
        }} />
    </div>
  </div>

  {#if deletable}
    <IconButton class="deleteButton" on:click={() => dispatch('delete', image)}>
      <InlineSVG src={removeIcon} />
    </IconButton>
  {/if}

  {#if cropable && showImageCropper}
    <ImageCropper
      imageName={image.name}
      imageUrl={image.url}
      on:close={() => (showImageCropper = false)}
      on:crop />
  {/if}

  {#if focusEditable && showImageFocuser}
    <ImageFocuser
      {image}
      on:close={() => (showImageFocuser = false)}
      on:setfocus />
  {/if}
{/if}
